import lodashCloneDeep from "lodash/cloneDeep";
import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import utc from "dayjs/plugin/utc";
dayjs.extend(advancedFormat);
dayjs.extend(utc);

import { ENDPOINT_ALERTS, ENDPOINT_ALERTED_DATA } from "@/common/constants/api";
import {
  httpGet,
  httpPost,
  httpDelete,
  httpPatch,
} from "@/services/api.service";
import {
  formatContentFeatures,
  formatAlertUiTOApi,
  formatAlertApiToUiObject,
} from "@/services/alerts/alerts.format.js";
import { getFormatted } from "@/common/date.utilities";
import {
  SOURCES,
  DEFAULT_SOURCES,
  ALERTED_CONTENT_ACTION_READ,
  ALERTED_CONTENT_ACTION_UNREAD,
} from "@/common/constants/alert.js";
import { hasTwitterAccess } from "@/services/user/user.service";
import { constructContentQuery } from "@/services/alerts/alerts.queryBuilder.js";
import { isFeatureUnlocked } from "@/common/featureLock.js";

export { _getAlerts as getAlerts };
export { _getContent as getContent };
export { _saveAlert as saveAlert };
export { _deleteAlert as deleteAlert };
export { _deleteSharedAlert as deleteSharedAlert };
export { _getDefaultAlertObject as getDefaultAlertObject };
export { _buildPreferences as buildPreferences };
export { _getTriggerRecords as getTriggerRecords };
export { _getSources as getSources };
export { _getDefaultSources as getDefaultSources };
export { _setAlertedContentAction as setAlertedContentAction };

const _getAlerts = async () => {
  try {
    const url = ENDPOINT_ALERTS;
    const apiAlertsData = await httpGet({ url: url, noCache: true });
    if (
      apiAlertsData &&
      typeof apiAlertsData === "string" &&
      apiAlertsData.length > 0
    ) {
      const { data } = JSON.parse(apiAlertsData);
      return data.results.map(formatAlertApiToUiObject);
    }
    return [];
  } catch (error) {
    console.error("Unable to retrieve alerts", error);
    return [];
  }
};

const _getContent = async (params) => {
  try {
    const url = ENDPOINT_ALERTED_DATA;

    const apiAlertsData = await httpGet({
      url: encodeURI(url + constructContentQuery(params)),
      noCache: true,
    });

    if (
      apiAlertsData &&
      typeof apiAlertsData === "string" &&
      apiAlertsData.length > 0
    ) {
      const { data } = JSON.parse(apiAlertsData);
      let alert = {
        total: data.metadata.total,
        count: data.metadata.count,
        prev: data.metadata.prev,
        next: data.metadata.next,
        data: data.features.map(formatContentFeatures),
        alertStats: data.metadata.alertStats,
      };
      return alert;
    }
    return {
      total: 0,
      count: 0,
      data: [],
    };
  } catch (error) {
    console.error("Unable to retrieve alerts", error);
    return {
      total: 0,
      count: 0,
      data: [],
    };
  }
};

const _getDefaultAlertObject = () => {
  return {
    id: null,
    name: null,
    owner: null,
    aoiFeature: {},
    frequency: {},
    expiration: {},
    stabilityThreshold: {},
    share: {},
    shareList: [],
    keywords: [],
    sources: [],
  };
};

const _postAlert = async (params) => {
  try {
    await httpPost({
      url: ENDPOINT_ALERTS,
      data: JSON.parse(JSON.stringify(params)),
    });
    return true;
  } catch (error) {
    console.error("Unable to create alert", error);
    return false;
  }
};

const _saveAlert = async (params) => {
  try {
    const newAlerts = formatAlertUiTOApi(params);
    return await Promise.all(
      newAlerts.map(_postAlert).map((p) => p.catch((e) => e))
    );
  } catch (error) {
    console.error("Unable to retrieve alerts", error);
    return false;
  }
};

const _deleteAlert = async (params) => {
  try {
    const query = "/" + params.id;
    await httpDelete({
      url: ENDPOINT_ALERTS + query,
      noCache: true,
    });
    return true;
  } catch (error) {
    console.error("Unable to delete alert", error);
    return false;
  }
};

const _buildPreferences = (params) => {
  let preferences = lodashCloneDeep(params.preferences);

  preferences.alerts = preferences.alerts || {};
  preferences.alerts.mapped = params.mapped || [];
  return preferences;
};

const _deleteSharedAlert = async (params) => {
  try {
    const query = "/" + params.unsubToken + "?isToken=true";
    await httpDelete({
      url: ENDPOINT_ALERTS + query,
      noCache: true,
    });
  } catch (error) {
    console.error("Unable to deleteSharedAlert from alert", error);
    return false;
  }
};

const _getTriggerRecords = async (params) => {
  try {
    let query = "?alertsIdFilter=" + params.alertsIdFilter.join(",");
    query += "&triggeredStats=only";
    if (params.start) {
      query += "&start=" + params.start;
    }
    if (params.end) {
      query += "&end=" + params.end;
    }
    if (params.aoiId) {
      query += "&aoiId=" + params.aoiId;
    }
    if (params.bounds) {
      query += "&bounds=" + params.bounds;
    }
    if (params.hasOwnProperty("geoOnly")) {
      query += "&geoOnly=" + params.geoOnly;
    }

    const httpResult = await httpGet({
      url: ENDPOINT_ALERTED_DATA + query,
      noCache: true,
    });

    const { data } = JSON.parse(httpResult);

    if (
      !data ||
      !data.metadata ||
      !data.metadata.triggeredAlerts ||
      !data.metadata.triggeredAlerts.length === 0
    ) {
      return [];
    }
    return data.metadata.triggeredAlerts.map((result) => {
      return {
        alertId: result.alertId,
        triggeredId: result.triggeredId,
        startTime: result.triggeredTime,
        startTimeFormatted: getFormatted(result.triggeredTime),
        timestamp_ms: parseInt(dayjs(result.triggeredTime).format("x")),
        timestamp_ms_utc: parseInt(dayjs.utc(result.triggeredTime).format("x")),
        message: result.triggeredMessage,
      };
    });
  } catch (error) {
    console.error("Unable to getTriggerRecords", error);
    return [];
  }
};

const _getSources = () => {
  let sources = lodashCloneDeep(SOURCES);
  const HAS_TWITTER = hasTwitterAccess();
  if (HAS_TWITTER) {
    sources.push({
      id: "twitter",
      display: "SOCIAL",
    });
  }
  sources.push({
    id: "verified-event",
    display: "VERIFIED",
  });
  return sources;
};

const _getDefaultSources = () => {
  let allsources = _getSources();
  let defaultSourceIds = DEFAULT_SOURCES;
  let defaultSources = [];
  for (let i = 0; i < allsources.length; i++) {
    if (defaultSourceIds.indexOf(allsources[i].id) !== -1) {
      defaultSources.push(allsources[i]);
    }
  }
  return defaultSources;
};

const _setAlertedContentAction = async (params) => {
  try {
    if (!params) {
      console.error("Unable to set alerted content action, params not set");
      return false;
    }
    console.log(
      params.action,
      ALERTED_CONTENT_ACTION_READ,
      ALERTED_CONTENT_ACTION_UNREAD
    );
    if (
      params.action !== ALERTED_CONTENT_ACTION_READ &&
      params.action !== ALERTED_CONTENT_ACTION_UNREAD
    ) {
      console.error(
        "Unable to set alerted content action, params.action not set"
      );
      return false;
    }
    if (!params.id) {
      console.error("Unable to set alerted content action, params.id not set");
      return false;
    }
    await httpPatch({
      url: `${ENDPOINT_ALERTED_DATA}/${params.action}/${params.id}`,
    });
    return true;
  } catch (error) {
    console.error("Unable to set alerted content action", error);
    return false;
  }
};
